import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { login as apiLogin } from "@/api/portalApi";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TOKEN_KEY = "portal_token";
const UNAUTHORIZED_EVENT = "portal-unauthorized";

function isTokenExpired(token: string): boolean {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]!)) as { exp?: number };
        return payload.exp !== undefined && Date.now() / 1000 > payload.exp;
    } catch {
        return true;
    }
}

interface AuthState {
    token: string | null;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [token, setToken] = useState<string | null>(() => {
        const stored = sessionStorage.getItem(TOKEN_KEY);
        if (stored && isTokenExpired(stored)) {
            sessionStorage.removeItem(TOKEN_KEY);
            return null;
        }
        return stored;
    });

    useEffect(() => {
        if (token) {
            sessionStorage.setItem(TOKEN_KEY, token);
        } else {
            sessionStorage.removeItem(TOKEN_KEY);
        }
    }, [token]);

    // Auto-logout on any 401 from the API layer
    useEffect(() => {
        const handler = () => setToken(null);
        window.addEventListener(UNAUTHORIZED_EVENT, handler);
        return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
    }, []);

    const login = useCallback(async (username: string, password: string) => {
        const response = await apiLogin(username, password);
        setToken(response.token);
    }, []);

    const logout = useCallback(() => {
        setToken(null);
    }, []);

    const value = useMemo<AuthState>(
        () => ({ token, isAuthenticated: Boolean(token), login, logout }),
        [token, login, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within <AuthProvider>");
    }
    return ctx;
}

function LoginOverlay() {
    const { login } = useAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError(null);
        setIsPending(true);
        try {
            await login(username, password);
        } catch {
            setError("Sign in failed. Check your credentials.");
        } finally {
            setIsPending(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-muted/30 px-4">
            <div className="w-full max-w-sm rounded-xl border bg-card shadow-lg">
                <div className="flex flex-col items-center gap-2 border-b px-6 py-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <h1 className="text-xl font-semibold">Athavita</h1>
                    <p className="text-sm text-muted-foreground">Platform Admin Portal</p>
                </div>
                <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4 px-6 py-6">
                    {error && (
                        <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {error}
                        </div>
                    )}
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="username">Username</Label>
                        <Input
                            id="username"
                            type="text"
                            autoComplete="username"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="admin"
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </div>
                    <Button
                        type="submit"
                        className="mt-1 w-full"
                        disabled={isPending || !username || !password}
                    >
                        {isPending ? "Signing in…" : "Sign in"}
                    </Button>
                </form>
            </div>
        </div>
    );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) return <LoginOverlay />;
    return <>{children}</>;
}

export function LogoutRedirect() {
    const navigate = useNavigate();
    useEffect(() => { navigate("/", { replace: true }); }, [navigate]);
    return null;
}
